import { TrackingService } from '@avanta/shared/services/tracking';
import { type TrackingData, TrackingEventsType } from '@avanta/shared/types/tracking';
import { getUserId } from '@avanta/shared/utils/storage';
import {
  type TrackChatAudioNoteRecordedParams,
  type TrackChatFileSharedParams,
  type TrackChatMessageSentParams,
  type TrackChatMessageUpdatedParams,
} from './useChatTracking.types';

export type {
  TrackChatAudioNoteRecordedParams,
  TrackChatFileSharedParams,
  TrackChatMessageSentParams,
  TrackChatMessageUpdatedParams,
} from './useChatTracking.types';

export interface UseChatTracking {
  trackChatMessageSent: (params: TrackChatMessageSentParams) => Promise<void>;
  trackChatMessageUpdated: (params: TrackChatMessageUpdatedParams) => Promise<void>;
  trackChatFileShared: (params: TrackChatFileSharedParams) => Promise<void>;
  trackChatAudioNoteRecorded: (params: TrackChatAudioNoteRecordedParams) => Promise<void>;
}

const trackEvent = async (
  eventType: TrackingEventsType,
  params: Partial<TrackingData>
): Promise<void> => {
  const userId = getUserId();
  if (!userId || userId === '0') return;

  try {
    await TrackingService.createTracking(userId, {
      event: eventType,
      data: {
        userId,
        timestamp: new Date().toISOString(),
        ...params,
      },
    });
  } catch {
    // Tracking must not block chat actions.
  }
};

export const useChatTracking = (): UseChatTracking => ({
  trackChatMessageSent: (params: TrackChatMessageSentParams) =>
    trackEvent(TrackingEventsType.CHAT_MESSAGE_SENT, params),
  trackChatMessageUpdated: (params: TrackChatMessageUpdatedParams) =>
    trackEvent(TrackingEventsType.CHAT_MESSAGE_UPDATED, params),
  trackChatFileShared: (params: TrackChatFileSharedParams) =>
    trackEvent(TrackingEventsType.CHAT_FILE_SHARED, params),
  trackChatAudioNoteRecorded: (params: TrackChatAudioNoteRecordedParams) =>
    trackEvent(TrackingEventsType.CHAT_AUDIO_NOTE_RECORDED, params),
});
